import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";

import { useQueryClient } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";

import {
  buildScopeId,
  getActiveJob,
  getJobsForScope,
  getNextPendingJob,
  uploadQueueReducer,
} from "./uploadQueueReducer";
import {
  getInitialUploadedFilename,
  getInvalidateQueryKeyForJob,
  runUploadJob,
  shouldAwaitRefreshBeforeCompletion,
} from "../utils/uploadRunners";

import type { UploadJob, UploadQueueContextValue } from "./uploadQueue.types";

// Filet pour les médias, chemin normal pour les publicités, qui n'acquittent pas.
const COMPLETED_JOB_MAX_AGE_MS = 120000;

const UploadQueueContext = createContext<UploadQueueContextValue | null>(null);

export function useUploadQueue(): UploadQueueContextValue {
  const context = useContext(UploadQueueContext);
  if (!context) {
    throw new Error(
      "useUploadQueue must be used within an UploadQueueProvider",
    );
  }
  return context;
}

export function UploadQueueProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [jobs, dispatch] = useReducer(uploadQueueReducer, []);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const purgeTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const queryClient = useQueryClient();

  // Orchestrateur : au plus un upload actif ; promeut le prochain `pending`.
  useEffect(() => {
    if (getActiveJob(jobs)) return;
    const next = getNextPendingJob(jobs);
    if (!next || controllersRef.current.has(next.id)) return;

    const controller = new AbortController();
    controllersRef.current.set(next.id, controller);
    dispatch({ type: "START", jobId: next.id });

    const onProgress = (progress: number): void => {
      dispatch({ type: "PROGRESS", jobId: next.id, progress });
    };

    runUploadJob(next, { onProgress, signal: controller.signal })
      .then(async (result) => {
        const queryKey = getInvalidateQueryKeyForJob(next);

        if (shouldAwaitRefreshBeforeCompletion(next)) {
          // Swallowed: a refetch that fails must not report a transfer that
          // succeeded as an error.
          await queryClient.invalidateQueries(queryKey).catch(() => undefined);
        } else {
          queryClient.invalidateQueries(queryKey);
        }

        dispatch({
          type: "COMPLETE",
          jobId: next.id,
          uploadedFilename: result.uploadedFilename,
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        dispatch({ type: "ERROR", jobId: next.id });
      })
      .finally(() => {
        controllersRef.current.delete(next.id);
      });
  }, [jobs, queryClient]);

  // Purge des jobs `completed` non acquittés par une page.
  useEffect(() => {
    const timers = purgeTimersRef.current;
    jobs.forEach((job) => {
      if (job.status === "completed" && !timers.has(job.id)) {
        const timer = setTimeout(() => {
          timers.delete(job.id);
          dispatch({ type: "REMOVE", jobId: job.id });
        }, COMPLETED_JOB_MAX_AGE_MS);
        timers.set(job.id, timer);
      }
    });
  }, [jobs]);

  // Garde `beforeunload` tant qu'un upload est en file ou en cours. On dépend
  // d'un booléen dérivé pour ne ré-enregistrer le listener qu'au changement
  // d'état (et non à chaque tick de progression).
  const hasInFlightUpload = jobs.some(
    (job) => job.status === "pending" || job.status === "uploading",
  );

  useEffect(() => {
    if (!hasInFlightUpload) return undefined;
    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = ""; // legacy browser support
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasInFlightUpload]);

  // Au démontage du provider : purge les timers et annule les uploads en cours.
  useEffect(() => {
    const timers = purgeTimersRef.current;
    const controllers = controllersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      controllers.forEach((controller) => controller.abort());
    };
  }, []);

  const value = useMemo<UploadQueueContextValue>(() => {
    const abortAndRemove = (jobId: string): void => {
      const controller = controllersRef.current.get(jobId);
      if (controller) controller.abort();
      controllersRef.current.delete(jobId);
      dispatch({ type: "REMOVE", jobId });
    };

    return {
      jobs,
      enqueue: (file, target) => {
        const baseJob = {
          id: uuidv4(),
          scopeId: buildScopeId(target),
          file,
          displayName: file.name,
          uploadedFilename: getInitialUploadedFilename(target.kind, file),
          mimeType: file.type,
          status: "pending" as const,
          progress: 0,
          navigatePath: target.navigatePath,
        };

        const job: UploadJob =
          target.kind === "advertisingVideo"
            ? {
                ...baseJob,
                kind: target.kind,
                campaignUuid: target.campaignUuid,
                languageUuid: target.languageUuid,
                projectSlug: target.projectSlug,
              }
            : { ...baseJob, kind: target.kind, mediaId: target.mediaId };

        dispatch({ type: "ENQUEUE", job });
      },
      cancel: abortAndRemove,
      remove: abortAndRemove,
      retry: (jobId) => dispatch({ type: "RETRY", jobId }),
      getJobsForScope: (scopeId) => getJobsForScope(jobs, scopeId),
      acknowledgeCompleted: (jobId) => {
        const timer = purgeTimersRef.current.get(jobId);
        if (timer) {
          clearTimeout(timer);
          purgeTimersRef.current.delete(jobId);
        }
        dispatch({ type: "REMOVE", jobId });
      },
    };
  }, [jobs]);

  return (
    <UploadQueueContext.Provider value={value}>
      {children}
    </UploadQueueContext.Provider>
  );
}
